import { z } from 'zod';
import type { Visibility } from '../../domain/entities/common.ts';
import {
  CommentSchema,
  isNarrowerOrEqual,
  mentionsIn,
  recordKey,
  type Comment,
} from '../../domain/entities/comment.ts';
import type { RecordRef } from '../../domain/entities/notification.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { MemberKind } from '../../ports/auth-port.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Commenting on a record.
 *
 * The audience is the whole of the difficulty. A comment inherits the
 * record's and may be narrowed — an internal-only note on an owner-visible
 * daily log is the case this feature exists for — but never widened, because
 * a comment the owner can see on a log they cannot is a leak of the log
 * through its own conversation. That is checked here and again in the rules.
 */
export class CommentError extends Error {
  readonly reason: 'invalid' | 'wider' | 'notYours';

  constructor(reason: 'invalid' | 'wider' | 'notYours', message: string) {
    super(message);
    this.name = 'CommentError';
    this.reason = reason;
  }
}

export const WriteCommentInputSchema = z.object({
  body: z.string().trim().min(1, 'A comment needs something in it').max(5_000),
  /** Absent to start a thread, present to reply to one. */
  parentId: z.string().min(1).optional(),
  /** Defaults to the record's own audience. */
  visibility: z.custom<Visibility>().optional(),
});
export type WriteCommentInput = z.input<typeof WriteCommentInputSchema>;

export interface CommentContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

export interface CommentTarget {
  readonly record: RecordRef;
  /** The record's own audience, which bounds the comment's. */
  readonly visibility: Visibility;
}

function repo(context: Pick<CommentContext, 'data' | 'companyId' | 'jobId'>) {
  return context.data.collection<Comment>(
    { companyId: context.companyId, jobId: context.jobId },
    'comments',
  );
}

export async function writeComment(
  context: CommentContext,
  target: CommentTarget,
  input: WriteCommentInput,
  people: readonly { readonly uid: string; readonly displayName?: string }[] = [],
): Promise<Comment> {
  const parsed = WriteCommentInputSchema.safeParse(input);
  if (!parsed.success) throw new CommentError('invalid', parsed.error.issues[0]!.message);

  const visibility = parsed.data.visibility ?? target.visibility;
  if (!isNarrowerOrEqual(target.visibility, visibility)) {
    throw new CommentError(
      'wider',
      'A comment cannot be shared more widely than the record it is on',
    );
  }

  if (parsed.data.parentId) {
    const parent = await repo(context).get(parsed.data.parentId);
    if (!parent) throw new NotFoundError('comments', parsed.data.parentId);
    // A reply joins an existing conversation, so it has to be about the
    // same record — otherwise a thread would span two records and belong
    // to neither.
    if (parent.recordKey !== recordKey(target.record)) {
      throw new CommentError('invalid', 'That reply belongs to a comment on another record');
    }
    if (!isNarrowerOrEqual(parent.visibility, visibility)) {
      throw new CommentError(
        'wider',
        'A reply cannot be shared more widely than the comment it answers',
      );
    }
  }

  const now = context.clock.now().toISOString();
  const comment: Comment = {
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    recordRef: target.record,
    recordKey: recordKey(target.record),
    parentId: parsed.data.parentId,
    body: parsed.data.body,
    visibility,
    mentionedUids: mentionsIn(parsed.data.body, people),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = CommentSchema.safeParse(comment);
  if (!checked.success) throw new CommentError('invalid', checked.error.issues[0]!.message);

  await repo(context).create(comment);
  return comment;
}

/**
 * Change what a comment says.
 *
 * Only its author, and `editedAt` is stamped so a reader can tell. A
 * conversation people rely on must not be rewritable in silence —
 * administrators can delete a comment, which is visible, rather than
 * editing one, which is not.
 */
export async function editComment(
  context: CommentContext,
  id: string,
  body: string,
  people: readonly { readonly uid: string; readonly displayName?: string }[] = [],
): Promise<Comment> {
  const parsed = WriteCommentInputSchema.shape.body.safeParse(body);
  if (!parsed.success) throw new CommentError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('comments', id);
  if (existing.createdBy !== context.uid) {
    throw new CommentError('notYours', 'Only the person who wrote a comment can edit it');
  }

  const now = context.clock.now().toISOString();
  const patch = {
    body: parsed.data,
    mentionedUids: mentionsIn(parsed.data, people),
    editedAt: now,
    updatedAt: now,
    updatedBy: context.uid,
  };
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'comments',
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...patch };
}

/**
 * Delete a comment.
 *
 * Its replies are left where they are: they become roots of their own
 * thread, which `threadComments` already handles. Cascading would delete
 * what other people wrote because of what one person wrote, and a reply
 * often carries the useful part of a conversation.
 */
export async function deleteComment(context: CommentContext, id: string): Promise<void> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('comments', id);
  await repo(context).delete(id);
}

/**
 * The two queries whose union is "the comments on this record you may see".
 *
 * The same shape and reason as everywhere else: rules are not filters.
 */
export function commentQueries(
  record: RecordRef,
  uid: string,
  kind: MemberKind = 'internal',
): readonly Query[] {
  const key = recordKey(record);
  const order = [{ field: 'createdAt', direction: 'asc' as const }];

  if (kind === 'sub' || kind === 'client') {
    const audience = kind === 'sub' ? 'visibility.subs' : 'visibility.owner';
    // One query: an external member has no private comments of their own to
    // fetch separately, and the rule's branch for them is exactly this.
    return [
      {
        where: [
          { field: 'recordKey', op: '==', value: key },
          { field: 'visibility.private', op: '==', value: false },
          { field: audience, op: '==', value: true },
        ],
        orderBy: order,
      },
    ];
  }

  return [
    {
      where: [
        { field: 'recordKey', op: '==', value: key },
        { field: 'visibility.private', op: '==', value: false },
      ],
      orderBy: order,
    },
    {
      where: [
        { field: 'recordKey', op: '==', value: key },
        { field: 'visibility.private', op: '==', value: true },
        { field: 'createdBy', op: '==', value: uid },
      ],
      orderBy: order,
    },
  ];
}

export function mergeComments(...pages: readonly (readonly Comment[])[]): Comment[] {
  const byId = new Map<string, Comment>();
  for (const page of pages) {
    for (const comment of page) byId.set(comment.id, comment);
  }
  return [...byId.values()].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
}

/**
 * The job's latest comments, newest first, whatever record they are on.
 *
 * `docs/features/10-client-portal.md` §2 asks the portal dashboard for
 * "recent comments: cards of latest comment threads (record type, date,
 * quoted comment)". That is the same collection `commentQueries` reads,
 * without the `recordKey` constraint — and the rule allows it, because
 * `listedCommentIsVisible` is about the document's audience and says
 * nothing about which record it belongs to.
 *
 * The audience constraints are the part that cannot be dropped. A query
 * that asked for every comment on the job would be refused outright, which
 * is the whole lesson of P3-04f and P4-20.
 *
 * `limit` is applied per query and then again after merging, because two
 * queries of ten can only be trimmed to ten once they are one list.
 */
export function recentCommentQueries(
  uid: string,
  kind: MemberKind = 'internal',
  limit = 5,
): readonly Query[] {
  const newestFirst = [{ field: 'createdAt', direction: 'desc' as const }];

  if (kind === 'sub' || kind === 'client') {
    const audience = kind === 'sub' ? 'visibility.subs' : 'visibility.owner';
    return [
      {
        where: [
          { field: 'visibility.private', op: '==', value: false },
          { field: audience, op: '==', value: true },
        ],
        orderBy: newestFirst,
        limit,
      },
    ];
  }

  return [
    {
      where: [{ field: 'visibility.private', op: '==', value: false }],
      orderBy: newestFirst,
      limit,
    },
    {
      where: [
        { field: 'visibility.private', op: '==', value: true },
        { field: 'createdBy', op: '==', value: uid },
      ],
      orderBy: newestFirst,
      limit,
    },
  ];
}

/** Newest first, which is the opposite of a thread's order. */
export function mergeRecentComments(
  limit: number,
  ...pages: readonly (readonly Comment[])[]
): Comment[] {
  return mergeComments(...pages)
    .reverse()
    .slice(0, limit);
}

/** The job's latest comments, live. Nothing is reported until all answer. */
export function subscribeToRecentComments(
  context: Pick<CommentContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
  onChange: (comments: readonly Comment[]) => void,
  onError: (cause: Error) => void,
  kind: MemberKind = 'internal',
  limit = 5,
): Unsubscribe {
  const queries = recentCommentQueries(context.uid, kind, limit);
  const pages: (readonly Comment[] | undefined)[] = queries.map(() => undefined);

  const emit = () => {
    const answered: (readonly Comment[])[] = [];
    for (const page of pages) {
      if (page === undefined) return;
      answered.push(page);
    }
    onChange(mergeRecentComments(limit, ...answered));
  };

  const stops = queries.map((query, index) =>
    repo(context).subscribe(
      query,
      (page) => {
        pages[index] = page.items;
        emit();
      },
      onError,
    ),
  );

  return () => {
    for (const stop of stops) stop();
  };
}

export async function listRecentComments(
  context: Pick<CommentContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
  kind: MemberKind = 'internal',
  limit = 5,
): Promise<Comment[]> {
  const pages = await Promise.all(
    recentCommentQueries(context.uid, kind, limit).map((query) => repo(context).list(query)),
  );
  return mergeRecentComments(limit, ...pages.map((page) => page.items));
}

export async function listComments(
  context: Pick<CommentContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
  record: RecordRef,
  kind: MemberKind = 'internal',
): Promise<Comment[]> {
  const pages = await Promise.all(
    commentQueries(record, context.uid, kind).map((query) => repo(context).list(query)),
  );
  return mergeComments(...pages.map((page) => page.items));
}

/** The same, live. Nothing is reported until both queries have answered. */
export function subscribeToComments(
  context: Pick<CommentContext, 'data' | 'companyId' | 'jobId' | 'uid'>,
  record: RecordRef,
  onChange: (comments: readonly Comment[]) => void,
  onError: (cause: Error) => void,
  kind: MemberKind = 'internal',
): Unsubscribe {
  const queries = commentQueries(record, context.uid, kind);
  const pages: (readonly Comment[] | undefined)[] = queries.map(() => undefined);

  const emit = () => {
    const answered: (readonly Comment[])[] = [];
    for (const page of pages) {
      if (page === undefined) return;
      answered.push(page);
    }
    onChange(mergeComments(...answered));
  };

  const stops = queries.map((query, index) =>
    repo(context).subscribe(
      query,
      (page) => {
        pages[index] = page.items;
        emit();
      },
      onError,
    ),
  );

  return () => {
    for (const stop of stops) stop();
  };
}

/**
 * The audiences a comment may be set to, given its record's.
 *
 * Offered as a list rather than four switches, for the same reason the file
 * audiences are: four toggles would let somebody build "owner only, not
 * internal", which reads as a mistake every time it appears.
 */
export interface CommentAudience {
  readonly value: string;
  readonly label: string;
  readonly visibility: Visibility;
}

export function audiencesFor(record: Visibility, kind: MemberKind = 'internal'): CommentAudience[] {
  // An external member cannot narrow. Offering a homeowner "internal only"
  // would offer them a comment they could not then read, and offering "only
  // me" a private note on somebody else's record. Their comment is simply
  // addressed to the same people the record is.
  if (kind !== 'internal') {
    return [{ value: 'inherit', label: 'Everybody on this record', visibility: record }];
  }
  return audiencesForInternal(record);
}

function audiencesForInternal(record: Visibility): CommentAudience[] {
  const all: CommentAudience[] = [
    {
      value: 'inherit',
      label: 'Same as the record',
      visibility: record,
    },
    {
      value: 'internal',
      label: 'Internal only',
      visibility: { internal: true, subs: false, owner: false, private: false },
    },
    {
      value: 'private',
      label: 'Only me',
      visibility: { internal: false, subs: false, owner: false, private: true },
    },
  ];
  // Only the ones the record permits, so a control cannot offer something
  // the use case will refuse.
  return all.filter((option) => isNarrowerOrEqual(record, option.visibility));
}
